import { useEffect, useMemo, useRef, useState } from 'react';
import { useClients, useCreateClient } from '../hooks/useApi';
import type { Client } from '../api/types';

interface ClientAutocompleteProps {
  /** clientId seleccionado (UUID) o null. */
  value: string | null;
  /** Callback con el clientId nuevo, o null si se vacía. */
  onChange: (clientId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Selector de cliente con autocomplete + opción "crear nuevo inline".
 *
 * Muestra el catálogo Client[] del workspace. Si el operador escribe un nombre
 * que no coincide, ofrece crearlo: dispara POST /clients y al éxito setea el
 * clientId del nuevo cliente. El parent recibe siempre el UUID, no el nombre.
 */
export function ClientAutocomplete({
  value,
  onChange,
  placeholder = 'Cliente (opcional)',
  disabled,
  className,
}: ClientAutocompleteProps) {
  const { data: clients } = useClients();
  const createClient = useCreateClient();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Sincronizar el text input con el cliente seleccionado (cuando viene clientId,
  // mostrar su nombre). Solo cuando el value cambia desde afuera.
  useEffect(() => {
    if (!value) {
      setText('');
      return;
    }
    const found = (clients ?? []).find((c) => c.id === value);
    if (found) setText(found.name);
  }, [value, clients]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo<Client[]>(() => {
    const q = text.toLowerCase().trim();
    if (!q) return clients ?? [];
    return (clients ?? []).filter((c) => c.name.toLowerCase().includes(q));
  }, [clients, text]);

  const exactMatch = (clients ?? []).find(
    (c) => c.name.toLowerCase() === text.toLowerCase().trim(),
  );
  const showCreateOption = !!text.trim() && !exactMatch && !createClient.isPending;

  const handleCreate = async () => {
    const newClient = await createClient.mutateAsync({ name: text.trim() });
    onChange(newClient.id);
    setText(newClient.name);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          // Si se vacía o cambia y ya no hay match exacto, se desselecciona el clientId.
          if (!e.target.value.trim()) onChange(null);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' && showCreateOption) {
            e.preventDefault();
            handleCreate();
          }
        }}
        placeholder={placeholder}
        disabled={disabled || createClient.isPending}
        className={
          className ??
          'w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-slate-100 focus:outline-none focus:border-vision'
        }
        autoComplete="off"
      />
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-xl max-h-64 overflow-y-auto z-30">
          {filtered.length > 0 && (
            <>
              <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-800">
                Clientes existentes
              </div>
              {filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange(c.id);
                    setText(c.name);
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-800 ${
                    c.id === value ? 'text-vision bg-slate-800/50' : 'text-slate-100'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </>
          )}
          {showCreateOption && (
            <button
              type="button"
              onClick={handleCreate}
              className="w-full px-3 py-2 text-left text-sm text-vision hover:bg-slate-800 border-t border-slate-800"
            >
              ＋ Crear nuevo cliente: <span className="font-semibold">"{text.trim()}"</span>
            </button>
          )}
          {createClient.isPending && (
            <div className="px-3 py-2 text-xs text-slate-400">Creando…</div>
          )}
          {createClient.error && (
            <div className="px-3 py-2 text-xs text-red-400">
              {createClient.error instanceof Error
                ? createClient.error.message
                : 'Error al crear cliente'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
